import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Spinner, Modal, ModalBody } from 'reactstrap';
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { getAuthUser } from "../../../helpers/utils";
import { useTranslation } from 'react-i18next';
import Editor from "@monaco-editor/react";
import * as url from "../../../helpers/url_helper";
import { useDispatch } from 'react-redux';
import { updateDocumentJson } from '../../../slices/thunks';
import { toast } from 'react-toastify';


const JsonEditor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = getAuthUser();
  const { t, i18n } = useTranslation();
  let { state = {} } = useLocation();
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showError, setShowError] = useState(false);

    const handleSaveJson = async (d, pushToPinecone = false) => {
      if (isSaving) return;
      let data = {};

      try {
        data = JSON.parse(d);
      } catch (error) {
        setShowError(true);
        return
      }
      
      try {
        // const data = JSON.parse(d);
        setIsSaving(true);
        data.DocID = state.DocID;
        data.JsonURL = state.JsonURL;
        data.DocumentTypeID = state.DocumentTypeID;
        data.Status = state.Status;
        // data.Status = [1, 4, 98].includes(user.RoleID) ? 3 : 4;
        data.pushToPinecone = pushToPinecone;

        const payload = {
          DocID: state.DocID,
          JsonURL: state.JsonURL,
          DocumentTypeID: state.DocumentTypeID,
          Status: state.Status,
          // Status: [1, 4, 98].includes(user.RoleID) ? 3 : 4,
          pushToPinecone: pushToPinecone,
          documents: data
        }
  
        // const formData = new FormData();
        // formData.append("payload", payload);
  
        const response = await fetch(url.SAVE_DOC_DETAILS, {
          method: "POST",
          headers: { 'Content-Type': 'application/json' },
          // headers: { 'Content-Type': "text/plain" },
          body: JSON.stringify(payload), 
        });
  
        if (!response.body) throw new Error("No response body for stream");
  
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";
  
        // Process streaming response
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
  
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop(); // keep last incomplete chunk
  
          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;
  
            let eventName = "";
            let eventData = "";
  
            for (const line of eventBlock.split("\n")) {
              if (line.startsWith("event:"))
                eventName = line.slice(6).trim();
              else if (line.startsWith("data:"))
                eventData += line.slice(5).trim();
            }
  
            if (!eventName || !eventData) continue;
  
            try {
              const parsedData = JSON.parse(eventData);
  
              if (eventName === "file_saved") {
                // ✅ Update state safely
                if (state.JsonURL != parsedData.url) {
                  state.JsonURL = parsedData.url;
                  state.IsProcessed = true;
                  dispatch(updateDocumentJson(state))
                  navigate('/ai/json-editor', { 
                    state:  {
                      ...state
                    },
                    replace: true
                  })
                } else {
                  toast.success(t("DocumentDetailsSavedMessage"));
                }
                return parsedData;
              }
            } catch (err) {
              console.warn("Invalid SSE event data:", eventData, err);
            }
          }
        }
      } catch (error) {
        console.error("Save stream error:", error);
      } finally {
        setIsSaving(false); // ✅ only after stream fully ends
      }
    }

    useEffect(() => {
      const fetchJson = async () => {
        try {
          if (!state.JsonURL) return;
            // const response = await fetch("https://storage.googleapis.com/rag-legal-docs-dev/final_output/1757680360186.json");
            const response = await fetch(state.JsonURL);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            const jsonData = await response.json() || {};
            
            const updatedData = {
              ...jsonData,
              articles: jsonData.articles?.sort((a, b) => a.article_number_numeric - b.article_number_numeric)
            } 

            setData(JSON.stringify(updatedData, null, 2));
            setIsLoading(false);
        } catch (err) {
          console.error("Error fetching JSON:", err);
        }
      };

      fetchJson();
    }, [state.JsonURL]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <BreadCrumb title={t("Document")} />
          </Col>
        </Row>
        <Row className="align-items-center mb-3">
            <Col md="6">
                <div className="d-flex gap-2 justify-content-start">
                    <Button color="secondary" onClick={() => navigate(-1)}>
                        <i className={`${i18n.language === 'en' ? "ri-arrow-left-line" : "ri-arrow-right-line"}`} />
                    </Button>
                </div>
            </Col>
            <Col md="6" className="text-end">
                <div className="d-flex gap-2 justify-content-end">
                    { ([1, 4, 98].includes(user.RoleID)) && <>
                        <Button color="success" onClick={() => handleSaveJson(data)} disabled={isSaving || isLoading}>
                            {t('Save')}
                        </Button> 
                    </>
                    }
                </div>
            </Col>
        </Row>
        <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
            <Col md={7}
            style={{
                height: "100%",
                overflowY: "auto", // vertical scrollbar for PDF
                direction: "ltr"
            }}>
            { isLoading ?
                <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                        style={{ height: "3rem", width: "3rem" }}
                        className="me-2"
                    >
                        Loading...
                    </Spinner>
                </div> :
                <Editor
                  defaultLanguage="json"
                  defaultValue={data}
                  theme="vs-dark"
                  onChange={(value) => setData(value)}
                />
            }
            </Col>
            <Col md={5}
                style={{
                height: "100%",
                overflowY: "auto",
                overflowX: "hidden",
                }}>
            <iframe
                src={state.FileURL || ''}
                width="100%"
                height="95%"
                title="PDF Viewer"
                style={{ minHeight: "600px" }}
                ></iframe>
            </Col>
        </Row>
      </Container>

      <Modal isOpen={showError} centered={true}>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('Invalid JSON format')}</h4>
              <p className="text-muted mx-4 mb-0">{t('Please check your syntax (missing commas, extra brackets, or incorrect quotes) and try again.')}</p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              data-bs-dismiss="modal"
              onClick={() => setShowError(false)}
            >
              {t("Close")}
            </button>
          </div>
        </ModalBody>
      </Modal>
      
    </div>
  )
}

export default JsonEditor
